import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { updateProfile } from '../../store/slices/authSlice';
import {
  Box, Typography, TextField, Button, Card, CardContent, Grid,
  CircularProgress, Alert, Divider, Chip
} from '@mui/material';
import { Person, Save } from '@mui/icons-material';

const CustomerProfile = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user, loading, error } = useSelector(state => state.auth);
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '', lastName: user?.lastName || '',
    email: user?.email || '', phone: user?.phone || '',
    company: user?.company || '', country: user?.country || ''
  });
  const [passwords, setPasswords] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [success, setSuccess] = useState('');

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setSuccess('');
    const result = await dispatch(updateProfile(formData));
    if (updateProfile.fulfilled.match(result)) setSuccess('Profile updated successfully!');
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwords.newPassword !== passwords.confirmPassword) return;
    try {
      const api = (await import('../../services/api')).default;
      await api.put('/auth/updatepassword', {
        currentPassword: passwords.currentPassword,
        newPassword: passwords.newPassword
      });
      setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setSuccess('Password changed successfully!');
    } catch (err) { console.error(err); }
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>{t('profile')}</Typography>

      <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
        <Person color="primary" />
        <Typography variant="body1">{user?.firstName} {user?.lastName}</Typography>
        <Chip label={user?.customerId} size="small" color="primary" />
        <Chip label={user?.role} size="small" />
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Profile Info */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>Personal Information</Typography>
          <Box component="form" onSubmit={handleProfileUpdate}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField fullWidth label={t('first_name')} value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label={t('last_name')} value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label={t('email')} value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label={t('phone')} value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Company" value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Country" value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })} />
              </Grid>
            </Grid>
            <Button type="submit" variant="contained" startIcon={<Save />} disabled={loading}
              sx={{ mt: 2 }}>
              {loading ? <CircularProgress size={20} /> : 'Save Changes'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>Change Password</Typography>
          <Box component="form" onSubmit={handlePasswordChange}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth label="Current Password" type="password" value={passwords.currentPassword}
                  onChange={(e) => setPasswords({ ...passwords, currentPassword: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="New Password" type="password" value={passwords.newPassword}
                  onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Confirm Password" type="password" value={passwords.confirmPassword}
                  onChange={(e) => setPasswords({ ...passwords, confirmPassword: e.target.value })} />
              </Grid>
            </Grid>
            <Button type="submit" variant="outlined" sx={{ mt: 2 }}>Change Password</Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CustomerProfile;
